import { z } from 'zod';

import { type Tag, tagsQuerySchema } from './tag.js';
import { isDefined } from '../../utils.js';
import { type BaseItem } from '../common.js';

export enum TemplateVisibility {
  Private = 'Private',
  Organization = 'Organization',
  Global = 'Global',
}

export interface TemplateTag {
  name: string;
  category: TagCategory;
}

interface WithThumbnailUrl {
  thumbnailUrl?: string;
}

export const CaseStudySchema = z.object({
  hyperlinkUrl: z.string(),
  imageUrl: z.string(),
  title: z.string(),
});
export type CaseStudy = z.infer<typeof CaseStudySchema>;

export const FlowDefinitionTemplateMetadataSchema = z.object({
  caseStudy: CaseStudySchema.optional(),
  featuredTimestamp: z.string().optional(),
  hasThumbnail: z.boolean().optional(),
  isFeaturedTemplate: z.boolean().optional(),
  overview: z.string().optional(),
});
export type FlowDefinitionTemplateMetadata = z.infer<typeof FlowDefinitionTemplateMetadataSchema>;

interface BaseFlowDefinitionTemplate {
  name: string;
  organizationId: string;
  /** Required: clones this flow definition (with subflows) to create the template. */
  sourceFlowDefinitionId: string;
  content: string;
  detailedContentMarkdown: string;
  createdById?: string;
  updatedById?: string;
  metadata?: FlowDefinitionTemplateMetadata;
  tags?: Tag[];
  visibility: TemplateVisibility;
}

export interface FlowDefinitionTemplate
  extends BaseFlowDefinitionTemplate, BaseItem, WithThumbnailUrl {
  flowDefinitionId: string | undefined;
  isBookmarked?: boolean;
}

type WithOptionalKeys<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>;

type FlowDefinitionUpdateValues = Omit<BaseFlowDefinitionTemplate, 'tags'> & {
  tags?: TemplateTag[];
};

export type CreateValues = WithOptionalKeys<FlowDefinitionUpdateValues, 'organizationId'>;
export type UpdateValues = Partial<Omit<FlowDefinitionUpdateValues, 'sourceFlowDefinitionId'>>;

export const findAllOptionsSchema = z.object({
  organizationId: z.array(z.string().uuid()).optional(),
  creatorId: z.string().uuid().optional(),
  tags: tagsQuerySchema
    .optional()
    .transform((val) => (isDefined(val) ? JSON.stringify(val) : undefined)),
  bookmarkedByUserId: z.string().uuid().optional(),
  queryUserId: z.string().uuid().optional(),

  limit: z.coerce.number().optional(),
  offset: z.coerce.number().optional(),
  orderBy: z.string().optional(),
  orderDirection: z.enum(['asc', 'desc']).optional(),
  visibility: z.array(z.nativeEnum(TemplateVisibility)).optional(),
});
export type FindAllOptions = z.input<typeof findAllOptionsSchema>;

export enum TagCategory {
  Custom = 'Custom',
  FlowType = 'FlowType',
  ReplaceVendor = 'ReplaceVendor',
  SolutionArea = 'SolutionArea',
  UseCase = 'UseCase',
}

export const thumbnailUploadSchema = z.object({
  thumbnailData: z.string().base64(),
  contentType: z.string(),
});
export type ThumbnailUploadRequest = z.infer<typeof thumbnailUploadSchema>;

export const thumbnailUploadResponseSchema = z.object({
  thumbnailUrl: z.string().url(),
});
export type ThumbnailUploadResponse = z.infer<typeof thumbnailUploadResponseSchema>;

export const caseStudyImageUploadSchema = z.object({
  caseStudyImageData: z.string().base64(),
  contentType: z.string(),
});
export type CaseStudyImageUploadRequest = z.infer<typeof caseStudyImageUploadSchema>;

export const caseStudyImageUploadResponseSchema = z.object({
  caseStudyImageUrl: z.string().url(),
});
export type CaseStudyImageUploadResponse = z.infer<typeof caseStudyImageUploadResponseSchema>;
